import { BracketsEllipsesIcon } from '@langchain/untitled-ui-icons';
import { Alert } from '@mui/joy';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Light as SyntaxHighlighter,
  createElement,
} from 'react-syntax-highlighter';
import js from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import yaml from 'react-syntax-highlighter/dist/esm/languages/hljs/yaml';
import {
  stackoverflowDark as darkTheme,
  stackoverflowLight as lightTheme,
} from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { Virtuoso } from 'react-virtuoso';

import { IconButton } from '@/design-system';
import { useColorScheme } from '@/hooks/useColorScheme';
import { cn } from '@/utils/tailwind';

import { MAX_CODE_LENGTH } from './constants';
import { SyntaxHighlighterRow } from './types';
import { calculateTotalHeight } from './utils';

SyntaxHighlighter.registerLanguage('javascript', js);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('json', json);

function Row({
  row,
  stylesheet,
  useInlineStyles,
}: {
  row: SyntaxHighlighterRow;
  stylesheet: any;
  useInlineStyles: boolean;
}) {
  return createElement({
    node: row,
    stylesheet,
    useInlineStyles,
  });
}

function VirtualizedRenderer({
  rows,
  stylesheet,
  useInlineStyles,
  fullHeight,
}: {
  rows: SyntaxHighlighterRow[];
  stylesheet: any;
  useInlineStyles: boolean;
  fullHeight?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const totalHeight = useMemo(() => {
    if (fullHeight) {
      return '100%';
    }
    return `${calculateTotalHeight(rows, containerWidth)}px`;
  }, [rows, fullHeight, containerWidth]);

  const style = useMemo(() => ({ height: totalHeight }), [totalHeight]);

  return (
    <div ref={containerRef} style={style}>
      <Virtuoso
        className="flex-grow"
        totalCount={rows.length}
        overscan={1}
        itemContent={(index) => {
          const row = rows[index];
          return (
            <Row
              row={row}
              stylesheet={stylesheet}
              useInlineStyles={useInlineStyles}
            />
          );
        }}
      />
    </div>
  );
}

function MinimalCode({
  code,
  language,
  setShowFull,
  fullHeight,
}: {
  code: string;
  language: string;
  setShowFull?: (showFull: boolean) => void;
  fullHeight?: boolean;
}) {
  const [ignoreWarning, setIgnoreWarning] = useState(false);
  const [textSelected, setTextSelected] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const colorScheme = useColorScheme();

  // Important: All hooks must be called unconditionally, in the same order, on every render
  const customTheme = useMemo(
    () => ({
      ...(colorScheme.isDarkMode ? darkTheme : lightTheme),
      'code[class*="language-"]': {
        ...(colorScheme.isDarkMode ? darkTheme : lightTheme)[
          'code[class*="language-"]'
        ],
        fontSize: '14px',
        background: 'none',
      },
      ...(fullHeight ? { height: '100%' } : {}),
    }),
    [colorScheme.isDarkMode, fullHeight]
  );

  const customStyle = useMemo(() => {
    return {
      background: 'none',
      padding: 0,
      border: 'none',
    };
  }, []);

  // Check for text selection
  const checkForSelection = () => {
    const selection = window.getSelection();
    if (
      selection &&
      selection.toString().trim().length > 0 &&
      selection.rangeCount > 0
    ) {
      try {
        const range = selection.getRangeAt(0);
        const container = containerRef.current;

        // Check if the selection is within our component
        if (container?.contains(range.commonAncestorContainer)) {
          setTextSelected(true);
          return;
        }
      } catch (e) {
        // Handle potential error if selection becomes invalid during the check
        console.error('Error checking selection:', e);
      }
    }

    setTextSelected(false);
  };

  // Add mouseup event listener to detect text selection
  useEffect(
    () => {
      const container = containerRef.current;
      if (container) {
        container.addEventListener('mouseup', checkForSelection);

        // Clear selection state when component unmounts
        return () => {
          container.removeEventListener('mouseup', checkForSelection);
        };
      }
    },
    [
      /* We're using a function defined in the component scope */
    ]
  );

  const lineCount = code?.split('\n').length ?? 0;

  // Determine what to render (warning or content)
  const showWarning = lineCount > MAX_CODE_LENGTH && !ignoreWarning;

  // Return the appropriate JSX based on the condition
  if (showWarning) {
    return (
      <Alert
        color="warning"
        variant="soft"
        className="!rounded-none !rounded-t-[7px]"
      >
        <span>
          This content might be too long to display in the editor.{' '}
          <a
            className="cursor-pointer underline"
            onClick={() => setIgnoreWarning(true)}
          >
            Open anyway
          </a>
        </span>
      </Alert>
    );
  }
  return (
    <div
      ref={containerRef}
      className={cn(
        'group relative flex flex-col overflow-y-auto rounded rounded-b-lg p-3.5',
        'bg-secondary',
        fullHeight && 'h-full'
      )}
      onBlur={() => setTextSelected(false)}
    >
      {setShowFull && (
        <div
          className={`absolute right-10 top-2 z-10 bg-background transition-opacity duration-300 ${
            textSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          data-testid="show-full"
        >
          <IconButton
            variant="outlined"
            size="sm"
            color={textSelected ? 'primary' : 'secondary'}
            onClick={() => setShowFull(true)}
            className={textSelected ? 'animate-pulse duration-1000' : ''}
            icon={BracketsEllipsesIcon}
            label="Show full editor"
            tooltipProps={{
              title: textSelected
                ? 'Switch to full editor to copy or highlight text'
                : 'Show full editor to collapse sections and show line numbers',
              side: 'bottom',
            }}
          />
        </div>
      )}
      <SyntaxHighlighter
        customStyle={customStyle}
        language={language}
        style={customTheme}
        wrapLongLines={true}
        className={fullHeight ? 'h-full' : ''}
        renderer={({ rows, stylesheet, useInlineStyles }) => (
          <VirtualizedRenderer
            rows={rows}
            stylesheet={stylesheet}
            useInlineStyles={useInlineStyles}
            fullHeight={fullHeight}
          />
        )}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

export { MinimalCode };
